import type { DefaultContext } from '@apollo/client'
import { useContext, useMemo } from 'react'

import { EnvironmentContext } from 'config/environment'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { isAbsoluteURL } from 'modules/core/lib/isAbsoluteURL'

const createProductCatalogQueryContext = (
  useService: boolean,
  endpoint: string = '',
  extra?: Partial<DefaultContext>
): DefaultContext => {
  const uri = useService ? endpoint : undefined
  return { ...(uri ? { uri } : {}), ...extra }
}

export function useProductCatalog() {
  const { itManagementCommonServiceGraphqlEndpoint } = useContext(EnvironmentContext)
  const {
    has_it_management_v2_product_catalog_as_service,
    loadingFeatures: loadingProductCatalogContext,
  } = useFeatures(['it_management_v2_product_catalog_as_service'])

  return useMemo(
    () => ({
      queryContext: createProductCatalogQueryContext(
        has_it_management_v2_product_catalog_as_service,
        itManagementCommonServiceGraphqlEndpoint
      ),
      /** True while the feature flag that determines the GraphQL endpoint is still loading. Wait for this before running product-catalog queries so the first request uses the correct URL. */
      loadingProductCatalogContext,
      buildProductImageUrl: (imageUrl: string | null | undefined, fallbackUrl?: string) =>
        imageUrl ?? fallbackUrl ?? '',
      isProductCatalogUrl: (url: string) => isAbsoluteURL(url),
    }),
    [
      has_it_management_v2_product_catalog_as_service,
      itManagementCommonServiceGraphqlEndpoint,
      loadingProductCatalogContext,
    ]
  )
}
